import React, {useState} from 'react';
import {Stack, styled} from '@mui/material';
import {
  Body,
  Button,
  designSystemToken,
  Headline,
  Icon,
  Label,
} from '@lightricks/react-design-system';
import ActionsMenu, {
  MenuOption,
} from '@contentlab/components/actions-menu/ActionsMenu';
import DownloadProjectButton from '@contentlab/components/download-project-button';
import ProjectProgressBadge from '@contentlab/components/project-progress-badge';
import ReadyAssetsState from '@contentlab/components/ready-assets-state';
import {ROUTES} from '@contentlab/config/routesConstants';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useLiveEstimatedMinutes from '@contentlab/hooks/use-live-estimated-minutes';
import ProjectAction from '@contentlab/types/ProjectAction';
import {ProjectStatus} from '@contentlab/types/project';
import {ProjectProps} from '@contentlab/types/projectProps';
import {
  CONTENT_LAB_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import GradientProgressBar from '@/components/gradient-progress-bar';
import Link from '@/components/link';

interface ProjectHeaderProps {
  project: ProjectProps;
  shouldShowProgress: boolean;
  onRenameClick: (project: ProjectProps) => void;
  onDeleteClick: (project: ProjectProps) => void;
  onMoreActionsClick: () => void;
}

const Container = styled(Stack)(() => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  padding: '24px',
  gap: '64px',
  alignItems: 'center',
  borderRadius: '32px',
  backgroundColor: designSystemToken('semantic.bg.secondary'),
  border: `1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`,
}));

const ProjectNameContainer = styled(Stack)(() => ({
  flex: 1,
  flexDirection: 'row',
  gap: '16px',
  alignItems: 'flex-start',
}));

const ProgressContainer = styled(Stack)(() => ({
  flexDirection: 'column',
  gap: '12px',
  flex: 1,
  height: '100%',
}));

const ActionsContainer = styled(Stack)(() => ({
  flex: 1,
  flexDirection: 'row',
  justifyContent: 'flex-end',
  gap: '12px',
}));

const DownloadProjectButtonContainer = styled(Stack)(() => ({
  flex: 1,
  justifyContent: 'center',
  maxWidth: '200px',
  marginRight: '8px',
}));

function ProjectHeader({
  project,
  shouldShowProgress,
  onRenameClick,
  onDeleteClick,
  onMoreActionsClick,
}: ProjectHeaderProps) {
  const [downloadInProgress, setDownloadInProgress] = useState(false);
  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] =
    useState<HTMLElement | null>(null);

  const {remainingMinutes, progressPercentage} = useLiveEstimatedMinutes(
    project.estimatedCompletionTime,
    project.createdAt
  );

  const handleActionsClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    onMoreActionsClick();
    setActionsMenuAnchorEl(event.currentTarget);
  };

  const handleActionsClose = () => {
    setActionsMenuAnchorEl(null);
  };

  const menuOptions: MenuOption[] = [
    {
      id: ProjectAction.Rename,
      title: translate('Rename'),
      icon: 'Navigation-Edit',
      isDisabled: project.status !== ProjectStatus.READY,
      onClick: () => {
        onRenameClick(project);
      },
    },
    {
      id: ProjectAction.Delete,
      title: translate('Delete'),
      icon: 'Actions-Remove',
      isDisabled:
        project.status !== ProjectStatus.READY &&
        project.status !== ProjectStatus.FAILED,
      onClick: () => {
        onDeleteClick(project);
      },
    },
  ];

  return (
    <Container>
      <ProjectNameContainer>
        <ProjectsButton />
        <Stack direction="column" gap="4px">
          <Headline size="lg" color={designSystemToken('semantic.fg.primary')}>
            {project.name}
          </Headline>
          <Body size="lg" color={designSystemToken('semantic.fg.secondary')}>
            {`${translate(project.featureName)} ${translate('project')}`}
          </Body>
        </Stack>
      </ProjectNameContainer>
      {shouldShowProgress && (
        <ProgressDetails
          status={project.status}
          remainingMinutes={remainingMinutes}
          progressPercentage={progressPercentage ?? 99}
          totalFlows={project.state.totalFlows}
          readyCount={project.state.readyCount}
        />
      )}
      <ActionsContainer>
        <DownloadProjectButtonContainer>
          <DownloadProjectButton
            project={project}
            isDisabled={project.status !== ProjectStatus.READY}
            downloadInProgress={downloadInProgress}
            setDownloadInProgress={setDownloadInProgress}
          />
        </DownloadProjectButtonContainer>
        <Button
          appearance="neutral"
          size="large"
          mode="tinted"
          onClick={handleActionsClick}
          icon={<Icon name="Actions-More" appearance="neutral" size="medium" />}
        />
        <ActionsMenu
          menuOpen={!!actionsMenuAnchorEl}
          onCloseMenu={handleActionsClose}
          anchorEl={actionsMenuAnchorEl}
          anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
          menuOptions={menuOptions}
        />
      </ActionsContainer>
    </Container>
  );
}

function ProgressDetails({
  status,
  remainingMinutes,
  progressPercentage,
  totalFlows,
  readyCount,
}: {
  status: ProjectStatus;
  remainingMinutes: number | null;
  progressPercentage: number;
  totalFlows: number;
  readyCount: number;
}) {
  const projectProgressPercentage = [
    ProjectStatus.READY,
    ProjectStatus.FAILED,
  ].includes(status)
    ? 100
    : progressPercentage;

  return (
    <ProgressContainer>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap="12px"
      >
        <Label
          size="lg"
          color={designSystemToken('semantic.fg.neutral')}
          width="70px"
        >
          {translate(`${projectProgressPercentage?.toFixed(0)}%`)}
        </Label>
        <ProjectProgressBadge
          remainingMinutes={remainingMinutes}
          status={status}
          showIcon={
            ![ProjectStatus.READY, ProjectStatus.FAILED].includes(status)
          }
          sx={{
            padding: '4px 8px',
            borderRadius: '100px',
            backgroundColor: designSystemToken('semantic.bg.tertiary'),
          }}
        />
        <Stack
          direction="row"
          gap="4px"
          alignItems="center"
          width="70px"
          justifyContent="flex-end"
        >
          <ReadyAssetsState completed={readyCount} total={totalFlows} />
        </Stack>
      </Stack>
      <GradientProgressBar
        initialValue={projectProgressPercentage}
        value={projectProgressPercentage}
        backgroundColor={designSystemToken('semantic.bg.brand-secondary')}
        fillColor={designSystemToken('semantic.bg.brand')}
      />
    </ProgressContainer>
  );
}

function ProjectsButton() {
  const {uiInteraction} = useUiInteractionEvent();
  return (
    <Link to={`${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_PROJECTS}`}>
      <Button
        onClick={() => {
          uiInteraction({
            item_name: 'projects_button',
            interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECT,
            flow_name: FLOW_NAMES.CONTENT_LAB.PROJECT,
          });
        }}
        icon={
          <Icon name="Navigation-Projects" appearance="neutral" size="large" />
        }
        appearance="neutral"
        size="large"
        mode="tinted"
      />
    </Link>
  );
}

export default ProjectHeader;
